import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ManufacturingGateBanner } from "@/components/manufacturing/ManufacturingGateBanner";
import { ManufacturingLaborPayView } from "@/components/manufacturing/ManufacturingLaborPayView";
import { useManufacturingEntitlementGate } from "@/components/manufacturing/useManufacturingEntitlementGate";
import {
  ghostBtnClass,
  inputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { listManufacturingPlants } from "@/lib/manufacturingClient";
import { canEditCompanyLaborRates } from "@/lib/manufacturing/laborPayList";
import type { MfgPlant } from "@/lib/manufacturing/types";

export function ManufacturingLaborPayModePanel() {
  const auth = useAuth();
  const readOnly = !canEditCompanyLaborRates(auth.roles);
  const gate = useManufacturingEntitlementGate();
  const [navTick, setNavTick] = useState(0);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [plantId, setPlantId] = useState("");

  useEffect(() => {
    if (!gate.enabled) return;
    void listManufacturingPlants().then((res) => {
      if (!res.ok) return;
      const next = res.data.plants ?? [];
      setPlants(next);
      setPlantId((cur) => cur || next.find((p) => p.is_default)?.id || next[0]?.id || "");
    });
  }, [gate.enabled, navTick]);

  return (
    <div className="w-full space-y-4 text-left">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">Labor pay</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            {readOnly
              ? "Look up company rates, unmatched order lines, and invoices. You can stamp Labor OK. Rate changes stay with manufacturing and ops."
              : "Author company labor rates ($ × qty), resolve unmatched order lines, and invoice the labor company."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
            Plant
            <select
              className={inputClass}
              value={plantId}
              onChange={(e) => setPlantId(e.target.value)}
              disabled={!gate.enabled || plants.length === 0}
            >
              {plants.length === 0 ? <option value="">No plants</option> : null}
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.plant_code} · {p.name}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className={ghostBtnClass} onClick={() => setNavTick((n) => n + 1)}>
            Refresh
          </button>
        </div>
      </div>
      <ManufacturingGateBanner gate={gate} />
      {gate.enabled && plantId ? (
        <ManufacturingLaborPayView
          plantId={plantId}
          navTick={navTick}
          enabled={gate.enabled}
          readOnly={readOnly}
        />
      ) : null}
    </div>
  );
}
